import axios from 'axios';

import { Mixpanel } from '/@/core/mixpanel';
import settings from '/@/core/settings';
import { _, setActiveLanguage } from '/@/locale';
import { configureViews, getViewByPathname } from '/@/settings/views';
import store from '/@/store';
import { setResetCount, setTotalCount } from '/@/store/loading-counter/actions';
import {
    getAppInstance,
    getSessionConversion,
    getSessionCountry,
    getSessionLanguage,
    getUser,
} from '/@/store/session/selectors';
import { startPublicApiSync } from '/@/store/sync';
import { setInteractiveEvents, startUserTracking, stopUserTracking, trackEvent } from '/@/store/tracking';
import { createAction } from '/@/store/utils';
import { normalizeAuthEmail } from '/@/utils';
import { formatPhoneNumber, validatePhoneNumber } from '/@/utils/phone-numbers';
import { reverse, stripLocalePrefix } from '/@/utils/router';
import { resetCache } from '/@/utils/swr';
import { mpTrackEvent } from '../tracking/mixpanel-events';

export const axiosWC = axios.create({ withCredentials: true });

const views = configureViews();

export const axFetch = axios.create({ withCredentials: true }); // V2 for "fetchHelper"
axFetch.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 || error.response?.status === 503) {
            if (settings.urls.users.refreshToken !== error.config.url) {
                return refreshToken()
                    .then((_) => {
                        return axFetch.request(error.config);
                    })
                    .catch((_) => {
                        return Promise.reject(error);
                    });
            }
            // Refresh failed
            const view = getViewByPathname(stripLocalePrefix(location.pathname), views.views);
            if (view && !view.type) {
                location.replace('/');
            }
        }
        return Promise.reject(error);
    }
);

export const fetchHelper = async (url, config) => {
    try {
        const response = await axFetch[config.method](url, config.body);
        return { response, data: response.data };
    } catch (error) {
        if (error.response?.status === 400) {
            return { response: error.response, data: error.response.data };
        }
        throw new Error('Error de conexión.');
    }
};

export const setToken = createAction('SESSION:SET_TOKEN');

const sessionCacheKey = 'cultivio.session';

const sessionCache = {
    get: () => JSON.parse(window.localStorage.getItem(sessionCacheKey)),
    set: (session) => window.localStorage.setItem(sessionCacheKey, JSON.stringify(session)),
    clear: () => window.localStorage.removeItem(sessionCacheKey),
};

export const setUser = createAction('SESSION:SET_USER');

let refreshingCallPromise;
export const refreshToken = async () => {
    if (refreshingCallPromise) {
        return refreshingCallPromise;
    }

    refreshingCallPromise = axFetch.post(settings.urls.users.refreshToken, {}).finally(() => {
        refreshingCallPromise = undefined;
    });
    return refreshingCallPromise;
};

export const startSession = createAction('SESSION:START_SESSION', async ({ payload: session, dispatch }) => {
    if (session === undefined) return;
    setInteractiveEvents(false);
    try {
        const res = await fetchHelper(settings.urls.users.token, {
            method: 'get',
        });
        await dispatch(setToken(res.data.token));
    } catch (error) {}

    dispatch(setCountry({ country: session.country, interactive: false }));
    dispatch(setLanguage({ language: session.language, interactive: false }));
    dispatch(setUnits({ units: session.units, interactive: false }));

    dispatch(setUser(session.user));
    dispatch(setNitrogenRecommendationEnabled(session.nitrogenRecommendationEnabled));
    dispatch(setSoyMultipliersMonsoy(session.soyMultipliersMonsoy));
    dispatch(setSoyMultipliersAgroeste(session.soyMultipliersAgroeste));
    startUserTracking(session.user, session.country, session.language);
});

export const startAnonymousSession = createAction('SESSION:START_ANONYMOUS_SESSION', async ({ dispatch, getState }) => {
    setInteractiveEvents(false);

    const initialSession = typeof window !== 'undefined' ? window.INITIAL_SESSION || {} : {};

    dispatch(setConversion(getSessionConversion(getState())));
    dispatch(setCountry({ country: initialSession.country, interactive: false }));
    dispatch(setUnits({ units: initialSession.units, interactive: false }));

    const savedLanguage = localStorage.getItem('language-setted');
    if (savedLanguage && initialSession.language !== savedLanguage && ['es', 'en', 'pt'].includes(savedLanguage))
        dispatch(setLanguage({ language: savedLanguage, interactive: false }));
    else dispatch(setLanguage({ language: initialSession.language, interactive: false }));

    dispatch(setUser(undefined));
    stopUserTracking();
});

export const destroySession = createAction('SESSION:DESTROY_SESSION', async ({ dispatch }) => {
    sessionCache.clear();
    stopUserTracking();
    dispatch(setUser(undefined));
});

const getConversion = (params) => {
    const { conversionKey, conversionToken } = params;
    if (conversionKey && conversionToken) return { conversionKey, conversionToken };
    return {};
};

// Extract the weather units mapping to a reusable function
const getWeatherUnitsForSystem = (system) => {
    const weatherUnits = {
        metric: { speed: 'km/h', temperature: '°C', rain: 'mm' },
        imperial: { speed: 'mph', temperature: '°F', rain: 'in' },
    };
    return weatherUnits[system] || weatherUnits.metric;
};

export const getSession = async () => {
    let session;
    try {
        // Validate session with backend.
        let sessionData;
        let unitsData;

        const [sessionRes, unitsRes] = await Promise.all([
            axFetch.get(settings.urls.users.session),
            axFetch.get(settings.urls.users.meUnits),
        ]);

        sessionData = sessionRes.data;
        unitsData = unitsRes.data;

        if (sessionData && unitsData) {
            const userData = {
                ...sessionData,
                internalProfileEnabled: sessionData.groups.some((el) => el.name === 'internal_profile'),
                internalAppInstances: sessionData.internal_app_instances.map((el) => el.id),
                app: settings.app.name,
            };

            // Check if country is in an App Instance, if not set the country of the first AI
            if (!settings.app.instances.some((ai) => ai.country === userData.country)) {
                sessionData.country = settings.app.instances?.[0]?.country;
            }

            const weatherUnits = getWeatherUnitsForSystem(unitsData.system);

            session = {
                user: userData,
                language: sessionData.language,
                country: sessionData.country,
                units: {
                    system: unitsData.system,
                    area: unitsData.area,
                    cornYield: unitsData.corn_yield,
                    cornDensity: unitsData.corn_density,
                    soyYield: unitsData.soy_yield,
                    soyDensity: unitsData.soy_density,
                    speed: weatherUnits.speed,
                    temperature: weatherUnits.temperature,
                    rain: weatherUnits.rain,
                },
                nitrogenRecommendationEnabled: sessionData.groups.some((el) => el.name === 'nitrogen_recommendation'),
                soyMultipliersMonsoy: sessionData.groups.some((el) => el.name === 'soy_multipliers_monsoy'),
                soyMultipliersAgroeste: sessionData.groups.some((el) => el.name === 'soy_multipliers_agroeste'),
            };
            sessionCache.set(session);
        } else {
            sessionCache.clear();
        }
    } catch (error) {
        console.warn(error); // eslint-disable-line no-console

        if (store().getState().network.offline) {
            session = sessionCache.get(); // Fallback to saved session in cache if network error.
        }
    }

    return session;
};

export const restoreSession = createAction('SESSION:RESTORE_SESSION', async ({ payload, dispatch }) => {
    const session = await getSession();
    setInteractiveEvents(false);

    dispatch(setResetCount());
    dispatch(setTotalCount(2));
    // --- end ---

    await Promise.allSettled([dispatch(startPublicApiSync())]);

    dispatch(setConversion(getConversion(payload.params)));

    if (session) {
        try {
            await dispatch(startSession(session));
            dispatch(setConverted('users_never_logged_in_v3'));
            return session;
        } catch (error) {
            await dispatch(destroySession());
            console.warn(error); // eslint-disable-line no-console
        }
    }

    // Always start an anonymous session to sync public data.
    await dispatch(startAnonymousSession());
});

export const login = createAction(
    'SESSION:LOGIN',
    async ({ payload: { email, password, onSuccess = resetCache }, getState, dispatch }) => {
        mpTrackEvent('Login began', undefined);
        const state = getState();
        const formData = new URLSearchParams();
        formData.append('username', normalizeAuthEmail(email));
        formData.append('password', password);
        formData.append('country', getSessionCountry(state).id);
        formData.append('language', getSessionLanguage(state));

        try {
            await axFetch.post(settings.urls.users.passwordLogin, formData);
            setInteractiveEvents(true);
            dispatch(trackEvent({ key: 'users.login' }));
            const session = await getSession();
            if (!session) {
                mpTrackEvent('Login', { state: 'fail' });
                throw new Error('Ha ocurrido un error...');
            }
            await dispatch(startSession(session));
            if (onSuccess) await onSuccess();
            dispatch(setConverted('users_never_logged_in_v3'));
            mpTrackEvent('Login', { state: 'success' });
        } catch (error) {
            if (error.response && error.response.status > 0 && error.response.status < 500) {
                mpTrackEvent('Login', {
                    state: 'fail',
                    error_detail: error.response.data.detail,
                });
                throw new Error(error.response.data.detail);
            }
            throw new Error(_('Ha ocurrido un error...'));
        }
    }
);

export const socialLogin = createAction(
    'SESSION:SOCIAL_LOGIN',
    ({ payload: { provider, next, history, redirectTo, source, onSuccess = resetCache }, getState, dispatch }) => {
        const state = getState();
        const conversion = getSessionConversion(state);
        const country = getSessionCountry(state).id;
        const language = getSessionLanguage(state);

        setInteractiveEvents(true);

        const nextUrl =
            settings.app.url +
            reverse('Profile', {
                qs: { next, ...conversion },
            });

        const providerUrl = `${settings.urls.users[provider]}?country=${country}&language=${language}&nextUrl=${nextUrl}`;

        dispatch(trackEvent({ key: 'users.socialLogin', provider }));
        mpTrackEvent('External provider connection started', {
            entry_source: source,
            provider,
        });

        const channel = new BroadcastChannel('slogin');

        window.open(providerUrl, 'slogin', 'location=1, status=1, scrollbars=1, width=800, height=800, popup=true');

        return new Promise((resolve, reject) => {
            /** @param {MessageEvent<any>} ev */
            setTimeout(
                () => {
                    channel.removeEventListener('message', messageReader);
                    reject(new Error(_('Vuelve a abrir el popup de social login')));
                },
                1000 * 60 * 2
            );
            const messageReader = (ev) => {
                if (ev.data.error) {
                    reject(new Error(_('Ha ocurrido un error...')));
                    channel.removeEventListener('message', messageReader);
                    mpTrackEvent('External provider connected', {
                        entry_source: source,
                        state: 'fail',
                        provider,
                    });
                }
                if (ev.data.success) {
                    mpTrackEvent('External provider connected', {
                        entry_source: source,
                        state: 'success',
                        provider,
                    });
                    resolve();
                    setTimeout(async () => {
                        const url = new URL(nextUrl);
                        await dispatch(trackEvent({ key: 'users.socialSignUp' }));
                        const session = await getSession();
                        await dispatch(startSession(session));

                        if (onSuccess) await onSuccess();

                        const appInstance = getAppInstance(getState());
                        const earlyReturn =
                            appInstance?.enableFieldviewBlockingModal && session?.user && !session.user.validated_type;
                        if (earlyReturn) return;

                        if (redirectTo) {
                            history(redirectTo);
                        } else {
                            if (!session.user.type) {
                                // If a user has no type it means is a social login
                                history(url.pathname, {
                                    params: { returnUrl: reverse('NewHome', { qs: { provider: provider } }) },
                                });
                            } else {
                                history(reverse('NewHome', { qs: { provider: provider } }));
                            }
                        }
                    });
                    channel.removeEventListener('message', messageReader);
                }
            };
            channel.addEventListener('message', messageReader);
        });
    }
);

export const linkFieldviewAccount = createAction(
    'SESSION:LINK_FIELDVIEW_ACCOUNT',
    ({ payload: { source, onSuccess }, dispatch }) => {
        setInteractiveEvents(true);

        const linkUrl = settings.urls.users.fieldviewLink;

        dispatch(trackEvent({ key: 'users.linkFieldviewAccount', provider: 'fieldview' }));
        mpTrackEvent('Fieldview account linking started', {
            entry_source: source,
        });

        const channel = new BroadcastChannel('slogin');

        window.open(linkUrl, 'slogin', 'location=1, status=1, scrollbars=1, width=800, height=800, popup=true');

        return new Promise<void>((resolve, reject) => {
            /** @param {MessageEvent<any>} ev */
            setTimeout(
                () => {
                    channel.removeEventListener('message', messageReader);
                    reject(new Error(_('Vuelve a abrir el popup de vinculación')));
                },
                1000 * 60 * 2
            );
            const messageReader = (ev: MessageEvent) => {
                if (ev.data.error) {
                    reject(new Error(_('Ha ocurrido un error...')));
                    channel.removeEventListener('message', messageReader);
                    mpTrackEvent('Fieldview account linking', {
                        entry_source: source,
                        state: 'fail',
                    });
                }
                if (ev.data.success) {
                    mpTrackEvent('Fieldview account linking', {
                        entry_source: source,
                        state: 'success',
                        fieldview_email: ev.data.fieldview_email,
                    });
                    resolve();
                    setTimeout(async () => {
                        if (onSuccess) await onSuccess(ev.data);
                    });
                    channel.removeEventListener('message', messageReader);
                }
            };
            channel.addEventListener('message', messageReader);
        });
    }
);

export const logout = createAction('SESSION:LOGOUT', async ({ dispatch, getState }) => {
    try {
        try {
            const state = getState();
            const language = getSessionLanguage(state);
            const country = getSessionCountry(state);
            Mixpanel.reset();
            Mixpanel.register({
                app: settings.app.name,
                language,
                country: country.id,
            });
        } catch {}

        axFetch.get(settings.urls.users.logout);
        await dispatch(destroySession());
    } catch (error) {}
});

export const signUp = createAction(
    'SESSION:SIGN_UP',
    async ({
        payload: {
            phone,
            firstName,
            lastName,
            email,
            password,
            type,
            subtype,
            receiveNotifications,
            receiveWhatsappNotifications,
            onSuccess = resetCache,
        },
        getState,
        dispatch,
    }) => {
        const state = getState();
        if (!validatePhoneNumber(phone)) throw new Error(_('El formato del número de teléfono es incorrecto.'));

        try {
            const res = await axFetch.post(settings.urls.users.signUp, {
                first_name: firstName,
                last_name: lastName,
                email: normalizeAuthEmail(email),
                password: password,
                type: type,
                subtype: subtype,
                receive_notifications: receiveNotifications,
                receive_whatsapp_notifications: receiveWhatsappNotifications,
                country: getSessionCountry(state).id,
                language: getSessionLanguage(state),
                phone: formatPhoneNumber(phone),
            });

            dispatch(trackEvent({ key: 'users.signUp' }));
            const session = await getSession();
            if (!session) throw new Error('Ha ocurrido un error...');
            await dispatch(startSession(session));
            if (onSuccess) await onSuccess();
            return res.data;
        } catch (error) {
            if (error.response.status === 400) {
                throw new Error(error.response.data.detail);
            }
            throw new Error(_('Ha ocurrido un error...'));
        }
    }
);

export const socialSignUp = createAction(
    'SESSION:SOCIAL_SIGN_UP',
    async ({
        payload: {
            phone,
            lastName,
            firstName,
            email,
            type,
            subtype,
            receiveNotifications,
            receiveWhatsappNotifications,
        },
        getState,
        dispatch,
    }) => {
        const state = getState();

        if (!validatePhoneNumber(phone)) throw new Error(_('El formato del número de teléfono es incorrecto.'));
        const { response, data } = await fetchHelper(settings.urls.users.socialSignUp, {
            method: 'post',

            body: JSON.stringify({
                last_name: lastName,
                first_name: firstName,
                email: normalizeAuthEmail(email),
                type: type,
                subtype: subtype,
                country: getSessionCountry(state).id,
                language: getSessionLanguage(state),
                receive_notifications: receiveNotifications,
                receive_whatsapp_notifications: receiveWhatsappNotifications,
                phone: formatPhoneNumber(phone),
            }),
        });

        if (response.ok) {
            await dispatch(trackEvent({ key: 'users.socialSignUp' }));
            const session = await getSession();
            await dispatch(startSession(session));
            resetCache();
            return data;
        }

        const { error } = data;
        const message = (error.email && error.email[0]) || (error.__all__ && error.__all__[0]);
        throw new Error(message);
    }
);

export const editProfile = createAction('SESSION:EDIT_PROFILE', async ({ payload, dispatch, getState }) => {
    const state = getState();
    const user = getUser(state);
    let phone = payload.phone;
    if (!validatePhoneNumber(phone)) throw new Error(_('El formato del número de teléfono es incorrecto.'));
    phone = formatPhoneNumber(phone);
    try {
        const res = await axFetch.patch(settings.urls.users.profile, {
            first_name: payload.firstName,
            last_name: payload.lastName,
            email: normalizeAuthEmail(payload.email),
            type: payload.type,
            subtype: payload.subtype,
            receive_notifications: payload.receiveNotifications,
            receive_whatsapp_notifications: payload.receiveWhatsappNotifications,
            show_slides: payload.showSlides,
            phone: phone,
        });
        dispatch(trackEvent({ key: 'users.profileUpdate' }));
        dispatch(setUser({ ...user, ...res.data }));
        return res.data;
    } catch (error) {
        if (error.response.status === 400) {
            throw new Error(error.response.data.detail);
        }
        throw new Error(_('Ha ocurrido un error...'));
    }
});

export const changePassword = createAction('SESSION:CHANGE_PASSWORD', async ({ payload, dispatch }) => {
    try {
        await axFetch.post(settings.urls.users.passwordChange, {
            current_password: payload.currentPassword,
            new_password: payload.password,
        });
        dispatch(trackEvent({ key: 'users.passwordChange' }));
        mpTrackEvent('Password change', { state: 'success' });
    } catch (error) {
        let errorMessage: string;
        if (error.response && error.response.status > 0 && error.response.status < 500) {
            errorMessage = error.response.data.detail;
        } else {
            errorMessage = _('Error inesperado');
        }
        mpTrackEvent('Password change', { state: 'fail', error_detail: error?.response?.data?.detail });
        throw Error(errorMessage);
    }
});

export const resetPassword = createAction('SESSION:RESET_PASSWORD', async ({ payload, dispatch }) => {
    try {
        const res = await axFetch.post(settings.urls.users.passwordReset, {
            email: normalizeAuthEmail(payload.email),
        });
        dispatch(trackEvent({ key: 'users.passwordReset' }));
        mpTrackEvent('Forgot Password requested', undefined);
    } catch (error) {
        if (error.response && error.response.status > 0 && error.response.status < 500) {
            throw Error(error.response.data.detail);
        }
        throw Error('Ha ocurrido un error...');
    }
});

export const confirmResetPassword = createAction('SESSION:CONFIRM_RESET_PASSWORD', async ({ payload, dispatch }) => {
    try {
        const res = await axFetch.post(settings.urls.users.passwordResetConfirm, {
            token: payload.token,
            email: normalizeAuthEmail(payload.email),
            new_password: payload.password,
        });
        dispatch(trackEvent({ key: 'users.passwordResetConfirm' }));
        mpTrackEvent('Forgot Password confirmed', undefined);
    } catch (error) {
        if (error.response && error.response.status > 0 && error.response.status < 500) {
            throw Error(error.response.data.detail);
        }
        throw Error('Ha ocurrido un error...');
    }
});

export const closeWalkthroughSlides = createAction(
    'SESSION:CLOSE_WALKTHROUGH_SLIDES',
    async ({ payload: showSlides, dispatch, getState }) => {
        const { user } = getState().session;
        const data = await dispatch(editProfile({ ...user, showSlides }));
        return data.showSlides;
    }
);

export const setCountry = createAction(
    'SESSION:SET_COUNTRY',
    ({ payload: { country, interactive = true }, getState, dispatch }) => {
        const { authenticated } = getState().session;
        if (interactive) {
            dispatch(trackEvent({ key: `users.${authenticated ? '' : 'anonymous.'}setCountry`, country: country }));
            localStorage.setItem('country-setted', country);
        }
        Mixpanel.register({ country });
        Mixpanel.people.set({ lastCountry: country });

        return country;
    }
);

export const setLanguage = createAction(
    'SESSION:SET_LANGUAGE',
    ({ payload: { language, interactive = true }, getState, dispatch }) => {
        const { authenticated } = getState().session;
        if (interactive) {
            if (language) localStorage.setItem('language-setted', language);
            dispatch(trackEvent({ key: `users.${authenticated ? '' : 'anonymous.'}setLanguage`, language: language }));
        }
        setActiveLanguage(language);
        Mixpanel.register({ language });
        Mixpanel.people.set({ lastLanguage: language });

        return language;
    }
);

export const setUnits = createAction(
    'SESSION:SET_UNITS',
    ({ payload: { units, interactive = true }, getState, dispatch }) => {
        const { authenticated } = getState().session;
        if (interactive)
            dispatch(trackEvent({ key: `users.${authenticated ? '' : 'anonymous.'}setUnits`, units: units }));
        return units;
    }
);

export const setPreferences = createAction('SESSION:SET_PREFERENCES', async ({ payload, getState, dispatch }) => {
    const state = getState();
    const { authenticated } = state.session;

    const { country, language, units, interactive } = payload;

    if (units) {
        // Update units with weather units based on the selected system
        const weatherUnits = getWeatherUnitsForSystem(units.system);

        const updatedUnits = {
            ...units,
            speed: weatherUnits.speed,
            temperature: weatherUnits.temperature,
            rain: weatherUnits.rain,
        };

        await dispatch(setUnits({ units: updatedUnits, interactive }));

        if (authenticated) {
            try {
                const response = await axFetch.put(settings.urls.users.meUnits, {
                    area: units.area,
                    system: units.system,
                    corn_yield: units.cornYield,
                    corn_density: units.cornDensity,
                    soy_yield: units.soyYield,
                    soy_density: units.soyDensity,
                });
            } catch (error) {
                throw new Error(_('Ha ocurrido un error...'));
            }
        }
    }

    if (country) await dispatch(setCountry({ country, interactive }));
    if (language) await dispatch(setLanguage({ language, interactive }));

    if (authenticated && (country || language)) {
        try {
            const response = await axFetch.patch(settings.urls.users.profile, {
                language,
                country,
            });
        } catch (error) {
            throw new Error(_('Ha ocurrido un error...'));
        }
    }
});

export const setReceiveNotifications = createAction(
    'SESSION:SET_RECEIVE_NOTIFICATIONS',
    async ({ payload: receiveNotifications, getState, dispatch }) => {
        const { user } = getState().session;
        const data = await dispatch(editProfile({ ...user, receiveNotifications }));
        // if fail editProfile raise error
        return data.receiveNotifications;
    }
);

export const setReceiveWhatsappNotifications = createAction(
    'SESSION:SET_RECEIVE_WHATSAPP_NOTIFICATIONS',
    async ({ payload: receiveWhatsappNotifications, getState, dispatch }) => {
        const { user } = getState().session;
        const data = await dispatch(editProfile({ ...user, receiveWhatsappNotifications }));
        // if fail editProfile raise error
        return data.receiveWhatsappNotifications;
    }
);

export const setConversion = createAction('SESSION:SET_CONVERSION');
export const resetConversion = createAction('SESSION:RESET_CONVERSION');

export const setConverted = createAction('SESSION:SET_IS_CONVERTED', ({ payload, dispatch, getState }) => {
    const { conversionKey, conversionToken } = getSessionConversion(getState());
    if (conversionKey != payload) return;

    dispatch(resetConversion());
    fetchHelper(settings.urls.conversions.setConverted, {
        method: 'post',
        credentials: 'same-origin',

        body: JSON.stringify({ token: conversionToken }),
    });
});

export const setNitrogenRecommendationEnabled = createAction(
    'SESSION:SET_NITROGEN_RECOMMENDATION_ENABLED',
    ({ payload }) => payload
);

export const setSoyMultipliersMonsoy = createAction('SESSION:SET_SOY_MULTIPLIERS_MONSOY', ({ payload }) => payload);

export const setSoyMultipliersAgroeste = createAction('SESSION:SET_SOY_MULTIPLIERS_AGROESTE', ({ payload }) => payload);
